import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, useMediaQuery, useTheme, Button, Dialog, IconButton } from "@mui/material";
import { Facebook, Twitter, Instagram, LinkedIn, YouTube, WhatsApp, Language } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import CampaignIcon from "@mui/icons-material/Campaign";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { getMenusPublic, getOffersPublic, getOffersPublicAll } from "../../../services/publicPegasApi/MakePublicOrder";
import { makeTableOrder, checkTableByQr } from "../../../services/OrderStatus/OrderStatusApi";
import { fetchSocialMediaLinks } from "../../../services/dashboard/SocialMediaLinksApi";
import { getPublicData } from "../../../services/publicPegasApi/MakePublicOrder";
import WhatsAppDialog from "./WhatsAppDialog";
import CustomerOrderTableHeader from "./CustomerOrderTableHeader";
import CategorySection from "./CategorySection";
import ProductsGrid from "./ProductsGrid";
import CartAndFooter from "./CartAndFooter";
import ProductDetailsModal from "./ProductDetailsModal";
import BannerSection from "./BannerSection";
import FooterSection from "./FooterSection";
import OnlineOffersTables from "./OnlineOffersTables";

const CustomerOrderTableMenu = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const [cart, setCart] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [menus, setMenus] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [savedCustomerData, setSavedCustomerData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSplash, setShowSplash] = useState(true);
  const [tableInfo, setTableInfo] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [publicData, setPublicData] = useState(null);
  const [publicDataLoading, setPublicDataLoading] = useState(true);
  const [publicDataError, setPublicDataError] = useState(null);
  const [offersOpen, setOffersOpen] = useState(false);
  const [hasOffers, setHasOffers] = useState(false);

  const subdomain = window.location.hostname.split(".")[0];
  const IMAGE_BASE_URL = `https://${subdomain}.restocash.app/`;
  const placeholderImage = "https://via.placeholder.com/300x140?text=No+Image";
  
  const pathParts = location.pathname.split("/").filter(Boolean);
  const qrCode = pathParts[pathParts.length - 1];

  // جلب البيانات عند التحميل
  // جلب القوائم
  useEffect(() => {
    const fetchMenus = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMenusPublic();
        setMenus(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        setError("فشل في جلب القوائم");
      } finally {
        setLoading(false);
      }
    };
    fetchMenus();
  }, []); // يتنفذ مرة واحدة عند فتح الصفحة

  // جلب العروض بعد ما القوائم تنزل
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const rootId = menus[0]?.root?.id;
        if (!rootId) return; // مفيش فرع لسه
        const res = await getOffersPublic(rootId);
        setOffers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("❌ خطأ في جلب العروض:", err);
      }
    };

    fetchOffers();
  }, [menus]); // يعتمد على القوائم علشان الفرع يكون معروف

  // جلب روابط وسائل التواصل الاجتماعي
  useEffect(() => {
    const fetchSocial = async () => {
      try {
        const response = await fetchSocialMediaLinks();
        const links = response?.data?.data || [];
        const activeLinks = links.filter(link => link.status === "active");
        setSocialLinks(activeLinks);
      } catch (err) {
        console.error("فشل في جلب روابط وسائل التواصل:", err);
      }
    };
    fetchSocial();
  }, []);

  // جلب البيانات العامة (لوجو، بانر، فوتر) مع معالجة كاملة للأخطاء
  useEffect(() => {
    const fetchPublicData = async () => {
      console.log("🚀 بدء useEffect لجلب البيانات العامة");
      setPublicDataLoading(true);
      setPublicDataError(null);
      
      try {
        const response = await getPublicData();
        console.log("📥 الاستجابة الكاملة:", response);
        
        // معالجة البيانات بناءً على الهيكل الفعلي
        let extractedData = null;
        
        // محاولة استخراج البيانات من مسارات مختلفة محتملة
        if (response?.data?.data) {
          extractedData = response.data.data;
          console.log("✅ البيانات من response.data.data:", extractedData);
        } else if (response?.data) {
          extractedData = response.data;
          console.log("✅ البيانات من response.data:", extractedData);
        } else if (response) {
          extractedData = response;
          console.log("✅ البيانات من response مباشرة:", extractedData);
        }
        
        if (extractedData) {
          console.log("🎯 البيانات النهائية المستخرجة:", {
            logo: extractedData.logo || "لا يوجد",
            banner: extractedData.banner_image || "لا يوجد",
            footer: extractedData.footer_image || "لا يوجد",
            phone: extractedData.phone || "لا يوجد",
            address: extractedData.address || "لا يوجد"
          });
          setPublicData(extractedData);
        } else {
          console.warn("⚠️ لم يتم العثور على بيانات في الاستجابة");
          setPublicData(null);
        }
      } catch (err) {
        console.error("❌ فشل في جلب البيانات العامة:", err);
        console.error("📍 نوع الخطأ:", err.name);
        console.error("📍 رسالة الخطأ:", err.message);
        
        if (err.response) {
          console.error("📍 حالة الاستجابة:", err.response.status);
          console.error("📍 بيانات الخطأ:", err.response.data);
        }
        
        setPublicDataError(err.message || "فشل في تحميل البيانات");
        setPublicData(null);
      } finally {
        setPublicDataLoading(false);
        console.log("✔️ انتهى useEffect لجلب البيانات العامة");
      }
    };

    fetchPublicData();
  }, []);

  // useEffect لمراقبة تغييرات publicData
  useEffect(() => {
    console.log("👁️ تحديث publicData:", publicData);
  }, [publicData]);

  // إخفاء السبلاتش بعد 1.2 ثانية أو عند انتهاء التحميل
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setShowSplash(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // تحقق من الطاولة عند تحميل الصفحة
  useEffect(() => {
    const fetchTableInfo = async () => {
      try {
        const res = await checkTableByQr(qrCode);
        setTableInfo(res);
      } catch (err) {
        console.error("❌ خطأ في جلب بيانات الطاولة:", err);
        setTableInfo(null);
      }
    };
    fetchTableInfo();
  }, [qrCode]);

  // جلب العروض للتحقق من وجودها
  useEffect(() => {
    const checkOffers = async () => {
      try {
        const res = await getOffersPublicAll();
        const list = res?.data ?? res;
        const offers = Array.isArray(list) ? list : [];
        setHasOffers(offers.length > 0);
      } catch (err) {
        console.error("فشل في جلب العروض:", err);
        setHasOffers(false);
      }
    };
    checkOffers();
  }, []);

  const handleAddToCart = (product) => {
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const handleConfirmProduct = (productWithDetails) => {
    setCart((prev) => [...prev, productWithDetails]);
    setProductModalOpen(false);
    setSelectedProduct(null);
  };

  const handleConfirmOrder = async () => {
    setConfirming(true);
    try {
      const formData = new FormData();
      formData.append("qrcode", qrCode);
      formData.append("root_id", branch?.id || "");
      formData.append("customer_name", "");
      formData.append("email", "");
      formData.append("customer_phone1", "");
      formData.append("customer_phone2", "");
      formData.append("customer_phone3", "");
      formData.append("customer_address1", "");
      formData.append("customer_address2", "");
      formData.append("customer_address3", "");

      cart.forEach((item, index) => {
        formData.append(`items[${index}][product_id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity || 1);
        formData.append(`items[${index}][price]`, item.price);
        formData.append(`items[${index}][note]`, item.note || "");
        formData.append(`items[${index}][status]`, "pending");
      });

      const result = await makeTableOrder(formData);
      const orderId = result?.data?.order_id || result?.order_id;
      
      setCreatedOrderId(orderId);
      setSavedCustomerData({
        customer_name: tableInfo?.table_name ? `طاولة ${tableInfo.table_name}` : "عميل",
        customer_phone1: "",
        customer_address1: branch?.address || "",
      });

      setShowSuccessMsg(true);
      setCart([]);
      setDrawerOpen(false);
      
      // فتح نافذة الواتساب إذا كان هناك أرقام واتساب
      const whatsappLinks = socialLinks.filter(
        link => link.type?.toLowerCase() === "whatsapp"
      );
      if (whatsappLinks.length > 0 && orderId) {
        setTimeout(() => {
          setShowWhatsAppDialog(true);
        }, 1500);
      }
    } catch (err) {
      console.error("❌ خطأ أثناء إرسال الطلب:", err);
      alert("حدث خطأ أثناء إرسال الطلب");
    } finally {
      setConfirming(false);
    }
  };

  const handleWhatsAppSent = () => {
    setShowWhatsAppDialog(false);
    setCreatedOrderId(null);
    setSavedCustomerData(null);
  };

  const handleWhatsAppSkip = () => {
    setShowWhatsAppDialog(false);
    setCreatedOrderId(null);
    setSavedCustomerData(null);
  };

  // Helper functions
  const getSocialIcon = (type) => {
    const iconProps = { sx: { fontSize: { xs: 28, sm: 32 } } };
    
    switch (type?.toLowerCase()) {
      case "facebook":
        return <Facebook {...iconProps} />;
      case "twitter":
        return <Twitter {...iconProps} />;
      case "instagram":
        return <Instagram {...iconProps} />;
      case "linkedin":
        return <LinkedIn {...iconProps} />;
      case "youtube":
        return <YouTube {...iconProps} />;
      case "whatsapp":
        return <WhatsApp {...iconProps} />;
      default:
        return <Language {...iconProps} />;
    }
  };

  const getSocialColor = (type) => {
    switch (type?.toLowerCase()) {
      case "facebook":
        return "#1877F2";
      case "twitter":
        return "#1DA1F2";
      case "instagram":
        return "#E4405F";
      case "linkedin":
        return "#0A66C2";
      case "youtube":
        return "#FF0000";
      case "whatsapp":
        return "#25D366";
      default:
        return "#757575";
    }
  };

  const getFilteredProducts = () => {
    if (!selectedCategory) return [];
    
    if (!searchQuery.trim()) {
      return selectedCategory.products;
    }

    const query = searchQuery.toLowerCase();
    return selectedCategory.products.filter(product => 
      product.name?.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query) ||
      product.price?.toString().includes(query)
    );
  };

  const getAllFilteredProducts = () => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase();
    const results = [];

    menus.forEach(menu => {
      const matchedProducts = menu.products.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.price?.toString().includes(query)
      );

      if (matchedProducts.length > 0) {
        results.push({
          category: menu.category,
          products: matchedProducts
        });
      }
    });

    return results.length > 0 ? results : null;
  };

  const selectedCategory = menus[tabIndex];
  const branch = menus[0]?.root;
  const categoryImage = selectedCategory?.category?.image
    ? `${IMAGE_BASE_URL}${selectedCategory.category.image}`
    : "https://via.placeholder.com/400x120?text=No+Category+Image";
  const filteredProducts = getFilteredProducts();
  const searchResults = getAllFilteredProducts();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fff",
        pb: 0,
        fontFamily: "Cairo, Arial, sans-serif",
        position: "relative",
      }}
    >
      {/* Splash Screen */}
      {showSplash && (
        <Box
          sx={{
            position: "fixed",
            zIndex: 2000,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            transition: "opacity 0.5s",
            opacity: showSplash ? 1 : 0,
          }}
        >
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: "50%",
              bgcolor: "#fffbe6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 24px 0 #ffb30022",
              mb: 3,
              animation: "splash-bounce 1.2s infinite",
            }}
          >
            <img
              src="https://cdn-icons-png.flaticon.com/512/3132/3132693.png"
              alt="logo"
              width={56}
              height={56}
              style={{ display: "block" }}
            />
          </Box>
          <Typography
            sx={{
              color: "#ffb300",
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: 2,
              mb: 1,
            }}
          >
            جاري التحميل...
          </Typography>
          <Box
            sx={{
              width: 60,
              height: 6,
              borderRadius: 3,
              bgcolor: "#ffb30022",
              overflow: "hidden",
              mt: 2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 6,
                bgcolor: "#ffb300",
                borderRadius: 3,
                animation: "splash-bar 1.2s infinite",
              }}
            />
          </Box>
          <style>
            {`
          @keyframes splash-bounce {
            0%, 100% { transform: translateY(0);}
            50% { transform: translateY(-18px);}
          }
          @keyframes splash-bar {
            0% { margin-left: 0; width: 30px;}
            50% { margin-left: 30px; width: 10px;}
            100% { margin-left: 0; width: 30px;}
          }
          `}
          </style>
        </Box>
      )}

      {/* Banner Section - مع معالجة حالة التحميل */}
      {!publicDataLoading && (
        <BannerSection 
          bannerImage={publicData?.banner_image || null}
          IMAGE_BASE_URL={IMAGE_BASE_URL}
        />
      )}

      {/* عرض رسالة خطأ إذا فشل تحميل البيانات العامة */}
      {publicDataError && !publicDataLoading && (
        <Box sx={{ 
          bgcolor: "#fff3cd", 
          color: "#856404",
          p: 1.5,
          textAlign: "center",
          borderBottom: "1px solid #ffeeba"
        }}>
          <Typography variant="body2">
            ⚠️ تعذر تحميل بعض البيانات: {publicDataError}
          </Typography>
        </Box>
      )}

      {/* Header and Search */}
      <CustomerOrderTableHeader
        tableInfo={tableInfo}
        branch={branch}
        cart={cart}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onCartOpen={() => setDrawerOpen(true)}
        searchResults={searchResults}
        isMobile={isMobile}
        logo={publicData?.logo || null}
        IMAGE_BASE_URL={IMAGE_BASE_URL}
        logoLoading={publicDataLoading}
      />

      {/* Category Section */}
      <CategorySection
        selectedCategory={selectedCategory}
        categoryImage={categoryImage}
        menus={menus}
        tabIndex={tabIndex}
        onTabChange={setTabIndex}
        searchQuery={searchQuery}
        isMobile={isMobile}
      />

      {/* Loading & Error */}
      {loading && (
        <Typography align="center" sx={{ mt: 6, color: "#888" }}>
          جاري التحميل...
        </Typography>
      )}
      {error && (
        <Typography color="error" align="center" sx={{ mt: 6 }}>
          {error}
        </Typography>
      )}

      {/* Products Grid */}
      {!loading && !error && (searchResults || selectedCategory) && (
        <ProductsGrid
          searchResults={searchResults}
          filteredProducts={filteredProducts}
          IMAGE_BASE_URL={IMAGE_BASE_URL}
          placeholderImage={placeholderImage}
          onAddToCart={handleAddToCart}
          isMobile={isMobile}
        />
      )}

      {/* Footer Section - مع معالجة حالة التحميل */}
      {!publicDataLoading && (
        <FooterSection
          footerImage={publicData?.footer_image || null}
          publicData={publicData || {}}
          IMAGE_BASE_URL={IMAGE_BASE_URL}
        />
      )}

      {/* Floating Offers Button */}
      {hasOffers && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 92, sm: 110 },
            left: { xs: 20, sm: 30 },
            zIndex: 1000,
          }}
        >
          <Button
            onClick={() => setOffersOpen(true)}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              height: { xs: 56, sm: 52 },
              px: { xs: 2, sm: 3 },
              boxShadow: "0 8px 30px rgba(118,75,162,0.4)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              animation: "offers-pulse 3s ease-in-out infinite",
              borderRadius: { xs: 28, sm: 26 },
              border: "3px solid rgba(255,255,255,0.3)",
              backdropFilter: "blur(10px)",
              fontFamily: "Cairo, Arial, sans-serif",
              fontWeight: 900,
              fontSize: { xs: 15, sm: 16 },
              textTransform: "none",
              "&:hover": {
                transform: "translateY(-6px) scale(1.05)",
                boxShadow: "0 12px 40px rgba(118,75,162,0.6)",
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                border: "3px solid rgba(255,255,255,0.5)",
              },
              "&::before": {
                content: '""',
                position: "absolute",
                top: -4,
                left: -4,
                right: -4,
                bottom: -4,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: { xs: 32, sm: 30 },
                opacity: 0.3,
                filter: "blur(8px)",
                zIndex: -1,
              },
            }}
            startIcon={
              <Box sx={{ position: "relative" }}>
                <LocalOfferIcon 
                  sx={{ 
                    fontSize: { xs: 24, sm: 26 },
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                  }} 
                />
                {/* Sparkle effect */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    width: 10,
                    height: 10,
                    bgcolor: "#ffd700",
                    borderRadius: "50%",
                    animation: "sparkle 2s ease-in-out infinite",
                    boxShadow: "0 0 8px #ffd700",
                  }}
                />
              </Box>
            }
          >
            العروض
          </Button>
        </Box>
      )}

      {/* Offers Dialog */}
      <Dialog
        open={offersOpen}
        onClose={() => setOffersOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: "100%", md: "85vh" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CampaignIcon sx={{ fontSize: 28, color: "#4a00e0" }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>العروض</Typography>
          </Box>
          <IconButton onClick={() => setOffersOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p: { xs: 1, sm: 2 }, height: { xs: "calc(100% - 64px)", md: "calc(85vh - 64px)" }, overflow: "auto" }}>
          <OnlineOffersTables />
        </Box>
      </Dialog>

      {/* Cart and Footer (Social Media) */}
      <CartAndFooter
        drawerOpen={drawerOpen}
        onDrawerClose={() => setDrawerOpen(false)}
        cart={cart}
        IMAGE_BASE_URL={IMAGE_BASE_URL}
        placeholderImage={placeholderImage}
        confirming={confirming}
        onConfirmOrder={handleConfirmOrder}
        showSuccessMsg={showSuccessMsg}
        onSuccessMsgClose={() => setShowSuccessMsg(false)}
        socialLinks={socialLinks}
        getSocialIcon={getSocialIcon}
        getSocialColor={getSocialColor}
        isMobile={isMobile}
      />

      {/* Product Details Modal */}
      <ProductDetailsModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        product={selectedProduct}
        onConfirm={handleConfirmProduct}
        IMAGE_BASE_URL={IMAGE_BASE_URL}
        placeholderImage={placeholderImage}
      />

      {/* WhatsApp Dialog */}
      <WhatsAppDialog
        open={showWhatsAppDialog}
        onClose={handleWhatsAppSkip}
        whatsappNumbers={socialLinks.filter(link => link.type?.toLowerCase() === "whatsapp")}
        orderId={createdOrderId}
        customerData={savedCustomerData}
        onSendToWhatsApp={handleWhatsAppSent}
      />

      {/* Animations */}
      <style>
        {`
          @keyframes offers-pulse {
            0%, 100% {
              box-shadow: 0 8px 30px rgba(118,75,162,0.4);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 8px 40px rgba(118,75,162,0.6), 0 0 0 10px rgba(118,75,162,0.1);
              transform: scale(1.02);
            }
          }

          @keyframes sparkle {
            0%, 100% {
              opacity: 0;
              transform: scale(0.5) rotate(0deg);
            }
            50% {
              opacity: 1;
              transform: scale(1) rotate(180deg);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default CustomerOrderTableMenu;
